import {
  arrow,
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  Placement,
  shift,
  useFloating,
} from '@floating-ui/react';
import classNames from 'classnames';
import { ReactNode, useRef } from 'react';
import { createPortal } from 'react-dom';

type Props = {
  children: ReactNode;
  isVisible: boolean;
  position?: string; // legacy positioning class for non-popper fallback
  anchorEl?: HTMLElement | null; // when provided, Floating UI is used
  placement?: Placement; // Floating UI placement, e.g. 'top', 'bottom-start'
  className?: string; // extra classes for the tooltip container
  withArrow?: boolean;
  centerArrow?: boolean; // center the arrow relative to tooltip box rather than target
};

export const Tooltip = ({
  children,
  isVisible,
  position,
  anchorEl,
  placement = 'top',
  className,
  withArrow = false,
  centerArrow = false,
}: Props) => {
  const arrowRef = useRef<HTMLDivElement>(null);

  const { refs, floatingStyles, middlewareData } = useFloating({
    elements: {
      reference: anchorEl || null,
    },
    placement,
    strategy: 'fixed',
    middleware: [
      offset(10),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      withArrow && !centerArrow
        ? arrow({ element: arrowRef, padding: 8 })
        : null,
    ].filter(Boolean),
    whileElementsMounted: anchorEl ? autoUpdate : undefined,
  });

  if (!isVisible) return null;

  // If an anchor element is provided, use Floating UI for positioning with optional arrow
  if (anchorEl) {
    const arrowData = middlewareData.arrow;
    const arrowStyle =
      withArrow && !centerArrow && arrowData
        ? {
            left: arrowData.x != null ? `${arrowData.x}px` : undefined,
            top: arrowData.y != null ? `${arrowData.y}px` : undefined,
          }
        : {};

    const content = (
      <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 10001 }}>
        <div
          className={classNames(
            'relative max-w-[380px] rounded-md bg-[#2d3131] px-3 py-2 text-xs leading-5 text-[#EEF1F1] elevation-1',
            className
          )}
        >
          {children}
          {withArrow && !centerArrow && (
            <div
              ref={arrowRef}
              style={arrowStyle}
              className='absolute h-3 w-3 before:absolute before:inset-0 before:h-3 before:w-3 before:rotate-45 before:bg-[#2d3131] before:content-[""]'
            />
          )}
          {withArrow && centerArrow && (
            <div
              className={classNames(
                'absolute h-3 w-3 before:absolute before:inset-0 before:h-3 before:w-3 before:rotate-45 before:bg-[#2d3131] before:content-[""]',
                placement?.startsWith('bottom')
                  ? '-top-1.5 left-1/2 -translate-x-1/2'
                  : placement?.startsWith('left')
                  ? '-right-1.5 top-1/2 -translate-y-1/2'
                  : placement?.startsWith('right')
                  ? '-left-1.5 top-1/2 -translate-y-1/2'
                  : '-bottom-1.5 left-1/2 -translate-x-1/2'
              )}
            />
          )}
        </div>
      </div>
    );

    const portalTarget = document.querySelector('#tooltip');
    return portalTarget ? (
      createPortal(content, portalTarget)
    ) : (
      <FloatingPortal>{content}</FloatingPortal>
    );
  }

  // Fallback legacy behavior (no popper)
  return (
    <div className='relative'>
      <p
        className={classNames(
          'absolute truncate rounded bg-[#2d3131] px-2 py-1 text-sm leading-4 text-[#eef1f1]',
          position ?? '-right-6 -top-6 ',
          className
        )}
      >
        {children}
      </p>
    </div>
  );
};
